import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import BottomBar from "./BottomBar";
import MainContent from "./MainContent";
import EditableText from "../../../../components/Texts/EditableText";
import { useFormContext } from "react-hook-form";
import {
  HISTORICAL_CHANGES,
  REPORTS,
  ERROR_GENERATING_CAMPAIGN,
  BUTTON_TRY_AGAIN,
  SYNCING_INFORMATION_NO_EDIT_CLARIFICATION,
  WAIT_FOR_CAMPAIGN_SYNC,
  AGENT_LABEL,
  CAMPAIGN_AGENT_BUTTON_TOOLTIP,
  CAMPAIGN_AGENT_BUTTON_TOOLTIP_PENDING_MANY,
  CAMPAIGN_AGENT_BUTTON_TOOLTIP_PENDING_ONE,
  ID_PLATFORM,
  READY,
  MODAL_CAMPAIGN_KPI_CPA_TITLE,
} from "../../../../i18n/keysTranslations";
import ButtonDuplicateCampaign from "../../../../components/Buttons/ButtonDuplicateCampaign";
import IconButton from "../../../../components/Buttons/IconButton";
import { openLink } from "../../../../utils/string";
import { useEffect, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslationApp } from "../../../../lib/i18next";
import Button from "../../../../components/Buttons/Button";
import Loader from "../../../../components/Loaders/Loader";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { ChartColumnBig, Sparkles, Target } from "lucide-react";
import ModalCampaingLogs from "../CampaignLogs/ModalCampaingLogs";
import ModalCampaignKpiCpa from "./ModalCampaignKpiCpa";
import AlertBanner from "../../../../components/Alerts/AlertBanner";
import { useSelectorApp } from "../../../../lib/redux";
import {
  ADS_CAMPAIGN_STATUS_DRAFT,
  ALERT_ICON_TYPE_SUCCESS,
} from "../../../../utils/constants";
import { hasRoleCapability } from "../../../../utils/roleCapabilities";
import {
  findAdIndexById,
  findAdsGroupIndexById,
  toAdsGroupsList,
} from "../../../../utils/adsGroupsList";
import { SimpleAlert } from "../../../../components/Alerts/Alerts";
import CampaignApprovalControls from "../CampaignApproval/CampaignApprovalControls";

const getSectionPlatformId = ({
  levelSectionSelected,
  sectionSelected,
  states,
  platformIDs,
  assetsGroups,
  adsGroups,
}) => {
  if (!sectionSelected) {
    return "";
  }

  if (levelSectionSelected === "platform") {
    const state = states?.[sectionSelected.platform];
    if (state === ADS_CAMPAIGN_STATUS_DRAFT) {
      return "";
    }
    return platformIDs?.[sectionSelected.platform] || "";
  }

  if (levelSectionSelected === "assetsGroup") {
    const assetsGroup = assetsGroups?.find(
      (item) => item?.id === sectionSelected.assetsGroupID
    );
    if (!assetsGroup || assetsGroup.status === ADS_CAMPAIGN_STATUS_DRAFT) {
      return "";
    }
    return assetsGroup.idPlatform || "";
  }

  if (levelSectionSelected === "adGroup") {
    const adGroup = adsGroups?.find(
      (item) => item?.id === sectionSelected.adGroupID
    );
    if (!adGroup || adGroup.status === ADS_CAMPAIGN_STATUS_DRAFT) {
      return "";
    }
    return adGroup.idPlatform || "";
  }

  if (levelSectionSelected === "ad") {
    const adGroup = adsGroups?.find(
      (item) => item?.id === sectionSelected.adGroupID
    );
    const ad = adGroup?.ads?.find((item) => item?.id === sectionSelected.adID);
    if (!ad || ad.status === ADS_CAMPAIGN_STATUS_DRAFT) {
      return "";
    }
    return ad.idPlatform || "";
  }

  return "";
};

const GeneralContainer = () => {
  const [name, setName] = useState("");
  const [isModalLogsOpen, setIsModalLogsOpen] = useState(false);
  const [isModalGoalsOpen, setIsModalGoalsOpen] = useState(false);
  const { t } = useTranslationApp();
  const { user } = useSelectorApp((state) => state.auth);
  const {
    isAllCampaignsPlatformCreated,
    setValue,
    watch,
    levelSectionSelected,
    sectionSelected,
    showErrorGenerating,
    lastInformationToGenerate,
    onGenerateCampaignPlatform,
    getValues,
    campaignID,
    isForcedSetupPage,
    isGeneralDisabledFields,
    hasSomePlatformCreated,
    onToggleChat,
    isChatVisible = false,
    pendingAgentChangesCount = 0,
    isSyncingData,
    isViewOnboarding,
    onChangeSectionSelected,
    campaignApproval,
    isEnablingCampaignApproval,
    onEnableCampaignApproval,
    onCopyCampaignApprovalLink,
    onOpenCampaignApprovalCommentsPanel,
  } = useFormContext();

  const platformsData = watch("platformsData") || {};
  const platforms = Object.keys(platformsData);
  const campaignPlatforms = watch("platforms") || [];
  const states = watch("states") || {};
  const platformIDs = watch("platformIDs") || {};
  const assetsGroups = watch("assetsGroups") || [];
  const adsGroups = watch("adsGroups") || [];
  const idPlatform = getSectionPlatformId({
    levelSectionSelected,
    sectionSelected,
    states,
    platformIDs,
    assetsGroups,
    adsGroups,
  });
  const showCampaignKpiButton =
    Boolean(name) &&
    levelSectionSelected === "platform" &&
    Boolean(campaignID) &&
    campaignPlatforms.length > 0 &&
    (!isGeneralDisabledFields || isSyncingData);
  const showCopyPlatformId = Boolean(idPlatform) || isSyncingData;
  const shouldShowCampaignApprovalControls =
    campaignPlatforms.length > 0 &&
    campaignPlatforms.every(
      (platform) => states[platform] === ADS_CAMPAIGN_STATUS_DRAFT
    );

  useEffect(() => {
    if (levelSectionSelected === "platform") {
      const platformData = platformsData[sectionSelected.platform];
      const nameCampaign = platformData?.name;
      const nameCampaigns = getValues("name");
      if (!nameCampaign && nameCampaigns) {
        setValue(
          `platformsData.${sectionSelected.platform}.name`,
          nameCampaigns,
          { shouldValidate: true, shouldDirty: true }
        );
        setName(nameCampaigns);
      } else {
        setName(nameCampaign);
      }
    }
    if (levelSectionSelected === "assetsGroup") {
      const assetsGroups = getValues("assetsGroups");
      const assetsGroup = assetsGroups.find(
        (assetsGroup) => assetsGroup.id === sectionSelected.assetsGroupID
      );
      const nameAssetGroup = assetsGroup.name;
      setName(nameAssetGroup);
    }
    if (levelSectionSelected === "adGroup") {
      const adsGroups = toAdsGroupsList(getValues("adsGroups"));
      const adGroup = adsGroups.find(
        (adGroup) => adGroup.id === sectionSelected.adGroupID
      );
      setName(adGroup?.name || "");
    }
    if (levelSectionSelected === "ad") {
      const adsGroups = toAdsGroupsList(getValues("adsGroups"));
      const adGroup = adsGroups.find(
        (adGroup) => adGroup.id === sectionSelected.adGroupID
      );
      const ad = adGroup?.ads?.find((ad) => ad.id === sectionSelected.adID);
      setName(ad?.name || "");
    }
    // eslint-disable-next-line
  }, [levelSectionSelected, sectionSelected]);

  const onChangeName = (newValue) => {
    const nameOpts = { shouldValidate: true, shouldDirty: true };
    if (levelSectionSelected === "platform") {
      setValue(
        `platformsData.${sectionSelected.platform}.name`,
        newValue,
        nameOpts
      );
      setValue(
        `platformsData.${sectionSelected.platform}.nameLowercase`,
        String(newValue ?? "")
          .trim()
          .toLowerCase(),
        { shouldValidate: true, shouldDirty: true }
      );
    }

    if (levelSectionSelected === "assetsGroup") {
      const assetsGroups = getValues("assetsGroups");
      const assetsGroupIndex = assetsGroups.findIndex(
        (assetsGroup) => assetsGroup.id === sectionSelected.assetsGroupID
      );
      setValue(`assetsGroups.[${assetsGroupIndex}].name`, newValue, nameOpts);
    }

    if (levelSectionSelected === "adGroup") {
      const adsGroups = toAdsGroupsList(getValues("adsGroups"));
      const adGroupIndex = findAdsGroupIndexById({
        adsGroups,
        adGroupID: sectionSelected.adGroupID,
      });
      if (adGroupIndex < 0) return;
      setValue(`adsGroups.[${adGroupIndex}].name`, newValue, nameOpts);
    }

    if (levelSectionSelected === "ad") {
      const adsGroups = toAdsGroupsList(getValues("adsGroups"));
      const adGroupIndex = findAdsGroupIndexById({
        adsGroups,
        adGroupID: sectionSelected.adGroupID,
      });
      if (adGroupIndex < 0) return;
      const adIndex = findAdIndexById({
        ads: adsGroups[adGroupIndex].ads,
        adID: sectionSelected.adID,
      });
      if (adIndex < 0) return;
      setValue(
        `adsGroups.[${adGroupIndex}].ads.[${adIndex}].name`,
        newValue,
        nameOpts
      );
    }
  };

  const onRemoveAlert = (platform, alertType, originalIndex) => {
    const currentPlatformData = platformsData[platform];
    if (
      !currentPlatformData?.alerts ||
      !Array.isArray(currentPlatformData.alerts)
    ) {
      return;
    }

    const targetEntity =
      levelSectionSelected === "platform" ? "campaign" : levelSectionSelected;
    const alertsArray = currentPlatformData.alerts;

    // Caso ideal: el índice original apunta exactamente a la alerta correcta
    const candidate = alertsArray[originalIndex];
    let indexToRemove = -1;
    if (
      candidate &&
      candidate.type === alertType &&
      candidate.entity === targetEntity
    ) {
      indexToRemove = originalIndex;
    } else {
      // Respaldo: buscar por coincidencia de type y entity
      indexToRemove = alertsArray.findIndex(
        (alert) => alert?.type === alertType && alert?.entity === targetEntity
      );
    }

    if (indexToRemove < 0) {
      return;
    }

    const updatedAlerts = [
      ...alertsArray.slice(0, indexToRemove),
      ...alertsArray.slice(indexToRemove + 1),
    ];

    setValue(`platformsData.${platform}.alerts`, updatedAlerts, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const renderLeftButtonGroup = (isMobile = false) => (
    <Box sx={{ display: "flex", gap: isMobile ? 1 : 1.5 }}>
      {name &&
        !isGeneralDisabledFields &&
        levelSectionSelected === "platform" && (
          <ButtonDuplicateCampaign campaignID={campaignID} />
        )}
      {showCampaignKpiButton && (
        <IconButton
          edge="end"
          disabled={isSyncingData}
          onClick={() => setIsModalGoalsOpen(true)}
          tooltipText={
            isSyncingData
              ? WAIT_FOR_CAMPAIGN_SYNC
              : MODAL_CAMPAIGN_KPI_CPA_TITLE
          }
          sx={{
            "&.Mui-disabled": {
              color: "text.disabled",
            },
          }}
        >
          <Target size={26} />
        </IconButton>
      )}
      {hasSomePlatformCreated && (
        <IconButton
          edge="end"
          onClick={() => {
            setIsModalLogsOpen(true);
          }}
          tooltipText={HISTORICAL_CHANGES}
        >
          <HistoryIcon
            sx={{
              width: 26,
              height: 26,
            }}
          />
        </IconButton>
      )}
    </Box>
  );

  const renderRightButtonGroup = () => (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        justifyContent: "flex-end",
      }}
    >
      {isSyncingData && (
        <Tooltip
          title={t(SYNCING_INFORMATION_NO_EDIT_CLARIFICATION)}
          arrow
          placement="bottom-start"
          componentsProps={{
            tooltip: {
              sx: {
                borderRadius: 3,
                mt: "0px !important",
                backgroundColor: "background.paper",
                border: `2px solid ${INPUT_COLOR_BORDER}`,
                color: (theme) => `${theme.palette.text.primary} !important`,
              },
            },
            arrow: {
              sx: {
                "&::before": {
                  color: "background.paper",
                  border: `2px solid ${INPUT_COLOR_BORDER}`,
                },
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mr: 2,
            }}
          >
            <Loader hasMarginTop={false} size={20} />
          </Box>
        </Tooltip>
      )}
      {!isViewOnboarding &&
      hasRoleCapability(user?.role, "canUseCampaignAgent") &&
      shouldShowCampaignApprovalControls ? (
        <CampaignApprovalControls
          approvalData={campaignApproval}
          isEnabling={isEnablingCampaignApproval}
          disabled={isGeneralDisabledFields}
          onEnableApproval={onEnableCampaignApproval}
          onCopyLink={onCopyCampaignApprovalLink}
          onViewComments={onOpenCampaignApprovalCommentsPanel}
        />
      ) : null}
      {isAllCampaignsPlatformCreated && (
        <Button
          startIcon={<ChartColumnBig size={20} />}
          color="secondary"
          onClick={() => {
            openLink(`/ads-campaign-statistics?campaignID=${campaignID}`);
          }}
        >
          {t(REPORTS)}
        </Button>
      )}

      {!isViewOnboarding &&
        hasRoleCapability(user?.role, "canUseCampaignAgent") && (
          <Badge
            badgeContent={pendingAgentChangesCount}
            invisible={isChatVisible || pendingAgentChangesCount === 0}
            slotProps={{
              badge: { "data-testid": "campaign-agent-pending-badge" },
            }}
            sx={{
              // Notification dot tucked onto the pill's top-right edge.
              "& .MuiBadge-badge": {
                top: 6,
                right: 12,
                minWidth: 20,
                height: 20,
                px: 0.5,
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1,
                border: "2px solid",
                borderColor: "background.paper",
                background: (theme) => theme.palette.button.primary.main,
                color: (theme) => theme.palette.button.primary.contrastText,
              },
            }}
          >
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Button
                startIcon={<Sparkles size={20} />}
                color="secondary"
                onClick={onToggleChat}
                tooltip={
                  pendingAgentChangesCount > 0
                    ? t(
                        pendingAgentChangesCount === 1
                          ? CAMPAIGN_AGENT_BUTTON_TOOLTIP_PENDING_ONE
                          : CAMPAIGN_AGENT_BUTTON_TOOLTIP_PENDING_MANY,
                        { count: pendingAgentChangesCount }
                      )
                    : t(CAMPAIGN_AGENT_BUTTON_TOOLTIP)
                }
                sx={{
                  minWidth: "max-content",
                }}
              >
                {t(AGENT_LABEL)}
              </Button>
            </Box>
          </Badge>
        )}
    </Box>
  );

  const onCopyIdPlatform = async () => {
    if (!idPlatform) {
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(idPlatform).catch(() => {});
      SimpleAlert({
        title: t(READY),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return;
    }
  };

  if (showErrorGenerating && isViewOnboarding) {
    return (
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h5">{t(ERROR_GENERATING_CAMPAIGN)}</Typography>
        <Button
          onClick={() => onGenerateCampaignPlatform(lastInformationToGenerate)}
        >
          {t(BUTTON_TRY_AGAIN)}
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          pt: levelSectionSelected ? 3 : 0,
        }}
      >
        {!isForcedSetupPage && (
          <Box
            sx={{
              mx: { xs: 1.75, md: 3 },
              mb: isSyncingData ? 2 : 0,
            }}
          >
            <AlertBanner
              platformsData={platformsData}
              sectionSelected={sectionSelected}
              levelSectionSelected={levelSectionSelected}
              currentLevel={levelSectionSelected}
              onRemoveAlert={onRemoveAlert}
            />

            {/* Layout for desktop */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {name && (
                  <EditableText
                    value={name}
                    variantText="h6"
                    disabledEditable={isGeneralDisabledFields}
                    onCallbackSave={onChangeName}
                    sx={{
                      maxWidth: 290,
                    }}
                    sxText={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                )}
                {renderLeftButtonGroup()}
              </Box>
              {renderRightButtonGroup()}
            </Box>

            {/* Layout for mobile */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {/* EditableText in its own row */}
              {name && (
                <EditableText
                  value={name}
                  variantText="h6"
                  disabledEditable={isGeneralDisabledFields}
                  onCallbackSave={onChangeName}
                  sx={{
                    maxWidth: "100%",
                  }}
                  sxText={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                />
              )}

              {/* Buttons in a separate row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {renderLeftButtonGroup(true)}
                {renderRightButtonGroup()}
              </Box>
            </Box>
            {showCopyPlatformId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.secondary",
                }}
              >
                {idPlatform ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mr: 2,
                    }}
                  >
                    {`${t(ID_PLATFORM, { value: t(sectionSelected.platform) })}: ${idPlatform}`}
                  </Typography>
                ) : null}
                <IconButton
                  edge="start"
                  disabled={isSyncingData || !idPlatform}
                  tooltipText={
                    isSyncingData || !idPlatform
                      ? WAIT_FOR_CAMPAIGN_SYNC
                      : undefined
                  }
                  onClick={onCopyIdPlatform}
                  sx={{
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": {
                      color: "text.secondary",
                    },
                    "&.Mui-disabled": {
                      color: "text.disabled",
                    },
                  }}
                >
                  <ContentCopyIcon
                    sx={{
                      fontSize: 13,
                    }}
                  />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        <MainContent />

        {platforms?.length > 0 && <BottomBar />}
      </Box>

      <ModalCampaignKpiCpa
        modalOpen={isModalGoalsOpen}
        onCloseModal={() => setIsModalGoalsOpen(false)}
        campaignID={campaignID}
        platforms={campaignPlatforms}
      />

      <ModalCampaingLogs
        modalOpen={isModalLogsOpen}
        onCloseModal={() => setIsModalLogsOpen(false)}
        enableReverse={true}
        getValues={getValues}
        setValue={setValue}
        onChangeSectionSelected={onChangeSectionSelected}
      />
    </>
  );
};

export default GeneralContainer;
